import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Editeur } from "./Editeur";
import { FicheEcran } from "./FicheEcran";
import { Journal } from "./Journal";
import { messageErreur } from "./libelles";
import { Outils } from "./Outils";
import { Recherche } from "./Recherche";
import type { EtatRecherche } from "./Recherche";
import { ErreurBase, appeler, deconnecter, envoyerLien, estConfigure, ouvrirSession } from "./supabase";
import type { Session } from "./supabase";

/**
 * Site d'administration du corpus (ADR-0005, #28).
 *
 * Personne ne voit rien sans être inscrit dans `administrateurs` : la base le
 * vérifie à chaque appel, le site ne fait que le refléter.
 */

export type Vue =
  | { readonly nom: "recherche" }
  | { readonly nom: "fiche"; readonly id: string }
  | { readonly nom: "nouvelle" }
  | { readonly nom: "journal" }
  | { readonly nom: "outils" };

type Etat =
  | { readonly nom: "chargement" }
  | { readonly nom: "deconnecte"; readonly erreurLien: string | null }
  | { readonly nom: "refuse"; readonly session: Session }
  | { readonly nom: "admin"; readonly session: Session; readonly signature: string };

export function App() {
  const [etat, setEtat] = useState<Etat>({ nom: "chargement" });

  const verifier = async (session: Session | null, erreurLien: string | null) => {
    if (session === null) {
      setEtat({ nom: "deconnecte", erreurLien });
      return;
    }
    try {
      const moi = await appeler<{ administrateur: boolean; nom: string | null }>("admin_moi");
      setEtat(moi.administrateur ? { nom: "admin", session, signature: moi.nom ?? "" } : { nom: "refuse", session });
    } catch (erreur) {
      const code = erreur instanceof ErreurBase ? erreur.code : "erreur_inconnue";
      setEtat({ nom: "deconnecte", erreurLien: code === "session_expiree" ? null : messageErreur(code) });
    }
  };

  useEffect(() => {
    void ouvrirSession().then(({ session, erreurLien }) => verifier(session, erreurLien));
  }, []);

  const quitter = () => {
    deconnecter();
    setEtat({ nom: "deconnecte", erreurLien: null });
  };

  if (!estConfigure()) {
    return (
      <main className="page etroite">
        <h1>Administration du corpus</h1>
        <p role="alert">Site construit sans adresse Supabase (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY).</p>
      </main>
    );
  }

  switch (etat.nom) {
    case "chargement":
      return (
        <main className="page etroite">
          <p role="status">Connexion…</p>
        </main>
      );
    case "deconnecte":
      return <Connexion erreurLien={etat.erreurLien} />;
    case "refuse":
      return (
        <main className="page etroite">
          <h1>Administration du corpus</h1>
          <p role="alert">
            Le compte <strong>{etat.session.email || "inconnu"}</strong> n'est pas administrateur. Rien n'est accessible.
          </p>
          <button type="button" onClick={quitter}>
            Se déconnecter
          </button>
        </main>
      );
    case "admin":
      return <Coquille session={etat.session} signature={etat.signature} onQuitter={quitter} />;
  }
}

function Connexion({ erreurLien }: { readonly erreurLien: string | null }) {
  const [email, setEmail] = useState("");
  const [envoi, setEnvoi] = useState<"repos" | "en_cours" | "envoye">("repos");
  const [erreur, setErreur] = useState<string | null>(erreurLien === null ? null : `Lien refusé : ${erreurLien}`);

  const envoyer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setEnvoi("en_cours");
    setErreur(null);
    try {
      await envoyerLien(email, window.location.origin + window.location.pathname);
      setEnvoi("envoye");
    } catch (e) {
      setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue"));
      setEnvoi("repos");
    }
  };

  return (
    <main className="page etroite">
      <h1>Administration du corpus</h1>
      <p className="discret">Réservé aux administrateurs de LexiReveal. Pas de mot de passe : un lien arrive par e-mail.</p>
      <form onSubmit={(e) => void envoyer(e)} className="pile">
        <label>
          Adresse e-mail
          <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <button type="submit" className="principal" disabled={envoi === "en_cours"}>
          Recevoir le lien de connexion
        </button>
      </form>
      {envoi === "envoye" && (
        <p role="status" className="succes">
          Si cette adresse est celle d'un administrateur, un lien vient de partir. Ouvre-le dans ce navigateur.
        </p>
      )}
      {erreur !== null && (
        <p role="alert" className="erreur">
          {erreur}
        </p>
      )}
    </main>
  );
}

function Coquille(props: { readonly session: Session; readonly signature: string; readonly onQuitter: () => void }) {
  const [vue, setVue] = useState<Vue>({ nom: "recherche" });
  const [recherche, setRecherche] = useState<EtatRecherche>({ texte: "", statut: "" });

  const onglets: readonly { readonly nom: Vue["nom"]; readonly libelle: string }[] = [
    { nom: "recherche", libelle: "Rechercher" },
    { nom: "nouvelle", libelle: "Nouvelle entrée" },
    { nom: "journal", libelle: "Journal" },
    { nom: "outils", libelle: "Export et jeu" },
  ];
  const actif = vue.nom === "fiche" ? "recherche" : vue.nom;

  return (
    <>
      <header className="entete">
        <div className="entete-haut">
          <span className="marque">LexiReveal · corpus</span>
          <span className="discret">
            {props.signature} ({props.session.email})
          </span>
          <button type="button" className="lien" onClick={props.onQuitter}>
            Se déconnecter
          </button>
        </div>
        <nav aria-label="Sections">
          {onglets.map((onglet) => (
            <button
              key={onglet.nom}
              type="button"
              className="onglet"
              aria-current={actif === onglet.nom ? "page" : undefined}
              onClick={() => setVue({ nom: onglet.nom } as Vue)}
            >
              {onglet.libelle}
            </button>
          ))}
        </nav>
      </header>
      <main className="page">
        {vue.nom === "recherche" && (
          <Recherche etat={recherche} onChange={setRecherche} onOuvrir={(id) => setVue({ nom: "fiche", id })} />
        )}
        {vue.nom === "fiche" && (
          <FicheEcran key={vue.id} id={vue.id} onRetour={() => setVue({ nom: "recherche" })} />
        )}
        {vue.nom === "nouvelle" && (
          <section>
            <h1>Nouvelle entrée</h1>
            <p className="discret">
              Elle naît en brouillon : les joueurs ne la verront qu'une fois publiée, depuis sa fiche.
            </p>
            <Editeur entree={null} onEnregistre={(id) => setVue({ nom: "fiche", id })} onAnnuler={() => setVue({ nom: "recherche" })} />
          </section>
        )}
        {vue.nom === "journal" && <Journal onOuvrir={(id) => setVue({ nom: "fiche", id })} />}
        {vue.nom === "outils" && <Outils />}
      </main>
    </>
  );
}
